import { useServices } from "@/services-context";
import { useTranslation } from "react-i18next";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Url } from "@/helpers/url";
import { useSuspenseLoader } from "@/helpers/suspense";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import { B2cCar, B2cProductParameter, B2cProductPayload, B2cProductPrice, ProductDocument } from "@/generated/graphql/b2c";
import { Services } from "@/services/services";
import { useBreadcrumbs } from "@/elements-react/fix-breadcrumb";
import { LeadFormCondition, ProductStateType } from "@/util/constants";
import { t, TFunction } from "i18next";
import { ImageResizer } from "@/util/image-resizer";
import { CART_LISTENER_NAME, InCartState } from "@/context/cart";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel";
import { stockType } from "@/constants/product-stock";
import ProductDispatch, { ProductDispatchItem } from "@/elements-react/product-dispatch";
import { Gtag } from "@/services/gtag";
import AddToCart from "@/elements-react/add-to-cart";
import FixQuantity from "@/elements-react/fix-quantity";
import PriceValue from "@/elements-react/price-value";
import FixBadge from "@/elements-react/fix-badge";
import ProductStatus from "@/elements-react/product-status";
import VatNotify from "@/elements-react/vat-notify";
import ShippingNotify from "@/elements-react/shipping-notify";
import ShippingRates from "@/elements-react/shipping-rates";
import FixText from "@/elements-react/fix-text";
import SiutableFor from "@/elements-react/siutable-for";
import { FixCarousel } from "@/elements-react/fix-carousel";
import { LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { CATEGORIES_LISTENER_NAME } from "@/services/categories";
import { createProductUrl } from "@/util/category";
import { OnRequest } from "@/elements-react/on-request";
import { ExchangesAddToCart } from "@/elements-react/exchanges-add-to-cart";
import ProductCanBe from "@/elements-react/product-can-be";
import { StoreSync } from "@/elements-react/product-area/store/sync";
import { useEffect } from "preact/hooks";
import { getCategoriesEcommerceItems, getItemName } from "@/helpers/google-events";
import { JsonLdDataset } from "@/services/json-ld";
import noImageProductUrl from "@/assets/no-image-product.svg";
import { SuggestPriceButton } from "@/elements-react/suggest-price-button";
import { CurrencyConverter } from "@/util/currency-converter";
import { defineOutOfStock, getFilteredCrosses, getSpecifications } from "@/helpers/product-manager";
import alertIcon from "@/assets/alert.svg";
import { getStandardDeliveryTermWindow } from "@/helpers/delivery-terms";

export type SituableForDatasetEntry = {
  brand: { id: string; name: string };
  model: { name: string; id: string };
  modification: { id: string; name: string };
  yearFrom: string;
  yearTo: string;
};

export type SituableForDataset = {
  list: Array<SituableForDatasetEntry>;
};

const getProductPayload = async (id: string, services: Services) => {
  const preloaded = services.cache.getProduct(id);

  if (preloaded) {
    return preloaded;
  }

  const response = await services.api.client.query({
    query: ProductDocument,
    variables: { input: { id } },
  });

  return response.data.Product as B2cProductPayload;
};

const getHasAvailableInStock = (product?: B2cProductPayload | null) => {
  return (
    (product &&
      product.prices.some((item: B2cProductPrice) => {
        return item.availableStockCount > 0;
      })) ??
    false
  );
};

const getAvailableForOrder = (product: B2cProductPayload) => {
  if (product.prices.length === 0) {
    return false;
  }

  return product.state === ProductStateType.Active || (product.state === ProductStateType.Discontinued && getHasAvailableInStock(product));
};

const getStockStatus = (
  availableForOrder: boolean,
  stockTypeProperty: string | null,
  price: B2cProductPrice,
  deliveryTermsMode: string,
) => {
  if (!availableForOrder) {
    return "discontinued";
  }

  const deliveryTerm = getStandardDeliveryTermWindow(price, deliveryTermsMode);
  const deliveryStandardTimesIsNotDefined = deliveryTerm.from == null && deliveryTerm.to == null;

  if (
    price.isHidePriceIfProductIsOutOfStock &&
    (stockTypeProperty === stockType.undefinedStock || deliveryStandardTimesIsNotDefined)
  ) {
    return "out-stock-not-available-for-order";
  }

  return ["local-stock", "local-distributor-stock"].includes(stockTypeProperty ?? "") ? "in-stock" : "out-stock";
};

const getBreadcrumbs = (product: B2cProductPayload, services: Services, route: string) => {
  const pathSegments = route.split("/");
  const categorySegments = pathSegments.slice(0, pathSegments.length - 1);
  const parentCategorySlug = categorySegments.join("/");

  const parentCategory =
    product.categories.find((category) => {
      const path = services.categories.getItemPathElements(category.id);
      const slug = path.map((item) => item.slug).join("/");

      return parentCategorySlug === slug;
    }) ?? product.categories[0];

  const path = parentCategory ? services.categories.getItemPathElements(parentCategory.id) : [];

  const breadcrumbs = path.map((item) => {
    const url = Url.to("dynamic-route", { dynamicPath: services.categories.getItemPath(item.id) });
    return {
      label: item.name,
      url: url,
    };
  });

  breadcrumbs.push({
    label: getTitle(product, "short"),
    url: Url.to("dynamic-route", { dynamicPath: pathSegments }),
  });

  return breadcrumbs;
};

const getTitle = (product: B2cProductPayload, prefer = "full" as "full" | "short") => {
  const [mpnFormatted, brandName, productName] = [getMpn(product), product!.brand.name, product!.productInfo.name].map((item) => {
    const value = item?.trim();
    return value?.length ? value : null;
  });

  const elements = prefer === "short" ? [mpnFormatted, productName] : [mpnFormatted, brandName, productName];

  return elements.filter(Boolean).join(" ");
};

const getMpn = (product: B2cProductPayload) => {
  return product!.mpnFormatted ?? product!.mpn;
};

const getSituableFor = (product: B2cProductPayload) => {
  const cars = product?.cars ?? [];
  const findDeep = (item: null | B2cCar, type: string) => {
    let current: null | B2cCar = item;

    while (current) {
      if (current.type === type) {
        return current;
      }
      current = current.parent ?? null;
    }

    return current;
  };

  const createSituableForEntry = (item: B2cCar) => {
    const modification = findDeep(item, "modification")!;
    const year = findDeep(item, "year")!;
    const model = findDeep(item, "model")!;
    const brand = findDeep(item, "brand")!;

    const [dateFrom, dateTo] = year.name.split("-").map((item) => item.trim());
    const [parsedFrom, parsedTo] = [dateFrom, dateTo].map((item) => item.match(/^(\d+)\.(\d+)/));
    const [yearFrom, yearTo] = [parsedFrom, parsedTo ?? parsedFrom].map((item) => parseInt(item![2]));

    return {
      brand: {
        id: brand.id,
        name: brand.name,
      },
      model: {
        id: model.id,
        name: model.name,
      },
      modification: {
        id: modification.id,
        name: modification.name,
      },
      yearFrom: yearFrom.toString(),
      yearTo: yearTo.toString(),
    } satisfies SituableForDatasetEntry;
  };

  return cars.map(createSituableForEntry);
};

const getImages = (product: B2cProductPayload) => {
  return (
    product.productAssets
      .filter((asset) => asset.type === "image" && asset.path)
      .map((asset) => ({
        ...asset,
        url: ImageResizer.getProductImageUrl(asset.path!, "300x300"),
        largeUrl: ImageResizer.getProductImageUrl(asset.path!, "original"),
        isGenerated: asset.isGenerated,
      })) ?? []
  );
};

const getMinQuantity = (type: "stock" | "product", product: B2cProductPayload) => {
  switch (type) {
    case "stock":
      return 1;
    case "product":
      return product.minimalOrderQuantity ?? 1;
  }
};

const getRedirectedMpn = (search: string, originalMpn?: string | null) => {
  const urlParams = new URLSearchParams(search);

  return originalMpn ?? urlParams.get("originalMpn");
};

function useRefState<T>(initial: T | (() => T)) {
  const [_key, setKey] = useState(0);
  const ref = useRef<T>(null!);
  if (!ref.current) {
    ref.current = typeof initial === "function" ? (initial as () => T)() : initial;
  }
  const update = useCallback(
    (value: T | ((prev: T) => T)) => {
      if (typeof value === "function") {
        ref.current = (value as (prev: T) => T)(ref.current);
      } else {
        ref.current = value;
      }

      setKey((prev) => prev + 1);
    },
    [setKey],
  );

  return [ref, update] as const;
}

export class ProductPageStore {
  sync: StoreSync;
  state: {
    registryUrl: string;
    product: { redirectUrl?: string } & B2cProductPayload;
    alternates: string[];
    originalMpn?: string | null;
  };

  constructor(protected services: Services) {
    this.state = {} as ProductPageStore["state"];
    this.sync = new StoreSync();
  }

  async loadForLocation(params: { location: LocationHook; route: RouteHook }) {
    const { location, route } = params;
    const registryUrl = Url.normalizeRoute(route.path);
    const detail = await this.services.routes.resolve(registryUrl, route.params.lang, false);
    const alternates = await this.services.routes.resolveAlternates(registryUrl);
    const product = await getProductPayload(detail!.id as string, this.services);

    this.state.registryUrl = registryUrl;
    this.state.product = product;
    this.state.alternates = alternates;
    this.state.originalMpn = this.services.preloaded.originalMpn;
  }
}

export function useProductPageCommon(view: LayoutTypes.TYPE_DESKTOP | LayoutTypes.TYPE_MOBILE, store: ProductPageStore) {
  const { t } = useTranslation();
  const route = useRoute();
  const location = useLocation();
  const services = useServices([CART_LISTENER_NAME, CATEGORIES_LISTENER_NAME, CUSTOMER_CHANNEL_LISTENER_NAME]);
  const type = {
    desktop: LayoutStyleTypes.TYPE_LG,
    mobile: LayoutStyleTypes.TYPE_SM,
  }[view] as LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;

  const [selections, setSelections] = useState({
    quantity: 1,
    dispatch: "1",
    variation: "1" as string | null,
  });

  const [isOverMaxQuantity, setIsOverMaxQuantity] = useState(false);
  const { product, alternates, registryUrl } = store.state;

  const breadcrumbs = useMemo(() => {
    return getBreadcrumbs(product, services, registryUrl);
  }, [product, services, registryUrl]);

  const seoMarkupDataset = useMemo(() => {
    return {
      type: "product",
      canonical: registryUrl,
      alternates: alternates,
    } as const;
  }, [product, registryUrl, alternates]);

  useBreadcrumbs(breadcrumbs);

  useEffect(() => {
    configureSelections(product!, null);
  }, [services.cart.items]);

  const getInCartState = (product: B2cProductPayload, priceId: string | null) => {
    return services.cart.getItem(product?.id, priceId);
  };

  const configureSelections = (product: B2cProductPayload, priceId: string | null) => {
    const inCart = getInCartState(product, priceId ?? product.prices?.[0]?.id);
    const updatedSelections = { ...selections };

    if (inCart.added && product.prices?.some((priceTmp) => priceTmp.id === inCart.priceId)) {
      updatedSelections.quantity = inCart.quantity;
      updatedSelections.dispatch = inCart.priceId!;
    } else {
      const priceNotInCart = product.prices.find((price) => {
        return price.id === priceId;
      });

      if (priceNotInCart) {
        updatedSelections.dispatch = priceNotInCart.id;
        updatedSelections.quantity = priceNotInCart.isLocalStockSupplier ? 1 : product.minimalOrderQuantity;
      } else {
        updatedSelections.dispatch = product.prices[0]?.id;
        updatedSelections.quantity = product.prices[0]?.isLocalStockSupplier ? 1 : product.minimalOrderQuantity;
      }
    }

    if (priceId) {
      const priceIndex = product?.prices.findIndex((price) => price.id === priceId);
      updatedSelections.variation = String(priceIndex! + 1);
    } else {
      updatedSelections.variation = "1";
    }

    setSelections(updatedSelections);
  };

  const getPriceDetails = (price: B2cProductPrice) => {
    const isStockSupplier = [stockType.localStock, stockType.localDistributorStock].includes(price.stockType);
    const isDistributorStock = price.stockType === stockType.localDistributorStock;
    const minOrderQuantity = isDistributorStock ? getMinQuantity("stock", product) : getMinQuantity("product", product);
    const maxOrderQuantity = price.isLocalStockSupplier ? price.availableStockCount ?? 0 : null;
    const factorOrderQuantity = isDistributorStock ? null : getMinQuantity("product", product);
    const cartItem = services.cart.getItem(product.id, price.id);

    return {
      price,
      isStockSupplier,
      minOrderQuantity,
      maxOrderQuantity,
      factorOrderQuantity,
      cartItem,
    };
  };

  const { prices } = useMemo(() => {
    const prices = {} as Record<string, ReturnType<typeof getPriceDetails>>;

    for (const price of product.prices) {
      prices[price.id] = getPriceDetails(price);
    }

    return {
      prices,
    };
  }, [product]);

  const getInitialPrice = () => {
    const cartItem = services.cart.getItem(product.id, null);
    const cardItemPrice = cartItem.added ? product.prices.find((price) => price.id === cartItem.priceId) : null;
    const initialPrice = product.prices[0]!;
    const price = cardItemPrice ?? initialPrice;

    return price;
  };

  const getQuantityRequested = (price: B2cProductPrice, cartItem: InCartState) => {
    const priceInitialQuantity = price.isLocalStockSupplier ? 1 : product.minimalOrderQuantity;
    const priceInCardQuantity = cartItem.added ? cartItem.quantity : null;
    const requestedQuantity = priceInCardQuantity ?? priceInitialQuantity;

    return requestedQuantity;
  };

  const getSelectedPrice = () => {
    if (!product) {
      return null;
    }

    return product.prices.find((price) => price.id === selections.dispatch);
  };

  const getQuantityCorrected = (params: {
    requestedQuantity: number;
    factorOrderQuantity: number | null;
    maxOrderQuantity: number | null;
    minOrderQuantity: number | null;
    check: { factorOrder?: boolean; maxOrder?: boolean; minOrder?: boolean };
  }) => {
    const { check, requestedQuantity, factorOrderQuantity, maxOrderQuantity, minOrderQuantity } = params;

    let quantity = requestedQuantity;
    const corrections = {
      factorOrder: false,
      maxOrder: false,
      minOrder: false,
    };

    const updateQuantity = (key: "factorOrder" | "maxOrder" | "minOrder", value: number) => {
      if (quantity !== value) {
        quantity = value;
        corrections[key] = true;
      }
    };

    if (check.factorOrder && factorOrderQuantity) {
      updateQuantity("factorOrder", Math.ceil(quantity / factorOrderQuantity) * factorOrderQuantity);
    }

    if (check.maxOrder && maxOrderQuantity && quantity > maxOrderQuantity) {
      updateQuantity("maxOrder", maxOrderQuantity);
    }

    if (check.minOrder && minOrderQuantity && quantity < minOrderQuantity) {
      updateQuantity("minOrder", minOrderQuantity);
    }

    return { quantity, corrections };
  };

  const dispatchItems = () => {
    const prices = product.prices ?? [];
    const items = prices?.map((price) => {
      const deliveryTerm = getStandardDeliveryTermWindow(price, services.customerChannel.deliveryTermsMode);

      return {
        id: price.id,
        name: price.supplierName,
        from: deliveryTerm.from,
        to: deliveryTerm.to,
        index: "",
      };
    }) as Array<ProductDispatchItem>;

    const acc = {} as Record<string, Array<{ index: string }>>;

    for (const item of items) {
      const key = `${item.from}-${item.to}`;
      const list = acc[key] ?? [];
      acc[key] = list.concat(item);
    }

    for (const list of Object.values(acc)) {
      const duplicated = list.length > 1;
      for (const [index, item] of list.entries()) {
        item.index = duplicated ? `#${index + 1}` : "";
      }
    }

    return items;
  };

  const isShipNextBusinessDay = () => {
    return [stockType.localStock, stockType.localDistributorStock].includes(getSelectedPrice()?.stockType ?? "");
  };

  const setDispatch = (event: CustomEvent<{ id: string }>) => {
    configureSelections(product!, event.detail.id);
    const { factorOrderQuantity, maxOrderQuantity } = getPriceDetails(price);
    if (factorOrderQuantity) {
      const value = Math.ceil(selections.quantity / factorOrderQuantity) * factorOrderQuantity;

      if (selections.quantity !== value) {
        selections.quantity = value;
        services.alerts.addMessages(
          [t("o45cqxotk22x53j3.factor-quantity-exceeded", "Quantity should be multiple of {{val}}", { val: factorOrderQuantity })],
          "warning",
        );
      }
    }

    if (maxOrderQuantity && selections.quantity > maxOrderQuantity) {
      selections.quantity = maxOrderQuantity;
      services.alerts.addMessages([t("2k1l3hf6f0682ddk.max-quantity-exceeded", "Quantity exceeds available stock")], "warning");
    }

    if (minOrderQuantity && selections.quantity < minOrderQuantity) {
      selections.quantity = minOrderQuantity;
      services.alerts.addMessages([t("42uv7ieg6f1d7spf.min-quantity-alert", "Product quantity is less than minimal order quantity")], "warning");
    }

    const dispatchTime = dispatchItems().find((item) => item.id === selections.dispatch);
    Gtag.pushEvent("dispatch_time_selection", { dispatchTime: dispatchTime?.from + "-" + dispatchTime?.to });
  };

  const setQuantity = (quantity: number) => {
    const { maxOrderQuantity } = getPriceDetails(price);

    Gtag.pushEvent("update_quantity", { quantity: quantity });

    const updatedSelections = { ...selections };

    updatedSelections.quantity = quantity;
    if (maxOrderQuantity && selections.quantity > maxOrderQuantity) {
      setIsOverMaxQuantity(true);
      updatedSelections.quantity = maxOrderQuantity;
      services.alerts.addMessages([t("2k1l3hf6f0682ddk.max-quantity-exceeded", "Quantity exceeds available stock")], "warning");
    }

    setSelections(updatedSelections);

    const inCart = getInCartState(product!, selections.dispatch);
    if (inCart?.added) {
      const quantity = updatedSelections.quantity;

      services.cart.updateItem({
        cartProductId: inCart?.cartProductId,
        productId: product!.id,
        priceId: selections.dispatch,
        quantity,
      });
    }
  };

  const navigateToProduct = (event: CustomEvent<{ id: string; reason: string }>) => {
    const { id, reason } = event.detail;
    const product = filteredCrosses.find((product) => product.id === id)!;
    const url = createProductUrl(product, services.categories);

    Gtag.pushEvent("analogue_click", id);
    location.route(url);
  };

  const renderCarousel = (type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM) => {
    return <FixCarousel items={images} type={type} />;
  };

  const renderCanBe = (type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM) => {
    const active = product!.id;
    const data = filteredCrosses;

    return <ProductCanBe type={type} active={active} data={data} />;
  };

  const renderTip = (className: string) => {
    return (
      <div className={className}>
        {t("l94wngk32e3g8lol.min-quantity-units", "min. {{quantity}} units", {
          quantity: minOrderQuantity,
        })}
        (<PriceValue amount={price.price.amount * minOrderQuantity} isVatApplicable={true}></PriceValue>)
      </div>
    );
  };

  const renderProductDiscontinuedSegment = (
    renderDiscontinuedStateDiscontinued: () => React.ReactNode,
    renderDiscontinuedStateOnRequest: () => React.ReactNode,
  ) => {
    const exchangesCount: number | undefined = product?.exchangesFinal.length;
    if (exchangesCount && exchangesCount === 1) {
      const productChange: B2cProductPayload | undefined = product?.exchangesFinal[0];

      if (productChange?.prices && productChange.prices.length > 0 && productChange.state === 1) {
        return renderDiscontinuedStateDiscontinued();
      }

      if (productChange && productChange.prices.length === 0 && [PRODUCT_STATE_ACTIVE, PRODUCT_STATE_ON_REQUEST].includes(productChange.state)) {
        return renderDiscontinuedStateOnRequest();
      }
    } else if (exchangesCount && exchangesCount > 1) {
      return renderDiscontinuedStateOnRequest();
    }
  };

  const renderDispatch = () => {
    return <ProductDispatch isShipNextBusinessDay={isShipNextBusinessDay()} dispatch={dispatchItems()} active={selections.dispatch} />;
  };

  const renderAdd = () => {
    if (!availableForOrder) {
      return <AddToCart type={LayoutStyleTypes.TYPE_LG} disabled={true} />;
    }

    const denyShowSuggestPriceButton = useMemo(() => {
      if (isAddedToCart || !price) {
        return false;
      }

      const priceWithVat = CurrencyConverter.applyVat(price.price, services.formatter.vatCoefficient);
      const priceForCompare = services.formatter.formatMoney(priceWithVat, "full", true);
      return parseFloat(priceForCompare) >= 1;
    }, [isAddedToCart, price]);

    return (
      <>
        <AddToCart type={LayoutStyleTypes.TYPE_LG} done={isAddedToCart} disabled={false} onClick={addToCartClick} />
        {denyShowSuggestPriceButton && <SuggestPriceButton originalProductPrice={price?.price} />}
      </>
    );
  };

  const getDescriptionText = () => {
    const text = product.productInfo?.description?.trim() ?? "";

    return text.length ? text : "";
  };

  const createProductDescription = (params: { primary: string; secondary?: string }) => {
    const lines = [] as string[];

    lines.push(params.primary);

    if (params.secondary?.length) {
      lines.push(`<div class="additionalInfo">${params.secondary}</div>`);
    }

    return lines.join(" ");
  };

  const renderAbout = (type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM) => {
    if (!product) {
      return null;
    }

    const primaryText = product.productInfo?.description?.trim() ?? "";

    const secondaryText = product.productInfoAdditional?.description?.trim() ?? "";
    const text = createProductDescription({ primary: primaryText, secondary: secondaryText });

    return <FixText type={type} text={text} showMore={false}></FixText>;
  };

  const renderDiscount = (divideCurrencySymbol = false) => {
    return <PriceValue amount={0} isVatApplicable={true} divideCurrencySymbol={divideCurrencySymbol} />;
  };

  const renderBadge = () => {
    return <FixBadge>Save {0}%</FixBadge>;
  };

  const renderQuantity = () => {
    const { factorOrderQuantity, maxOrderQuantity } = getPriceDetails(price);

    return (
      <FixQuantity
        type={type}
        quantity={selections.quantity}
        minQuantity={minOrderQuantity}
        maxQuantity={maxOrderQuantity ?? null}
        factorQuantity={factorOrderQuantity}
        onUpdateQuantity={setQuantity}
      />
    );
  };

  const [state, setState] = useRefState(() => {
    const price = getInitialPrice();

    if (!price) {
      return null;
    }

    const details = prices[price.id];
    const quantityRequested = getQuantityRequested(details.price, details.cartItem);
    const quantityCorrected = getQuantityCorrected({
      requestedQuantity: quantityRequested,
      factorOrderQuantity: details.factorOrderQuantity,
      maxOrderQuantity: details.maxOrderQuantity,
      minOrderQuantity: details.minOrderQuantity,
      check: {
        factorOrder: true,
        maxOrder: true,
        minOrder: true,
      },
    }).quantity;

    return {
      dispatch: price.id,
      quantity: quantityCorrected,
    };
  });

  const showMesssages = useCallback(
    (data: {
      factorOrderQuantity: number | null;
      corrections: {
        factorOrder: boolean;
        maxOrder: boolean;
        minOrder: boolean;
      };
    }) => {
      const messages = [] as string[];

      if (data.corrections.factorOrder) {
        messages.push(t("o45cqxotk22x53j3.factor-quantity-exceeded", "Quantity should be multiple of {{val}}", { val: data.factorOrderQuantity }));
      }

      if (data.corrections.maxOrder) {
        messages.push(t("2k1l3hf6f0682ddk.max-quantity-exceeded", "Quantity exceeds available stock"));
      }

      if (data.corrections.minOrder) {
        messages.push(t("42uv7ieg6f1d7spf.min-quantity-alert", "Product quantity is less than minimal order quantity"));
      }

      if (messages.length) {
        services.alerts.addMessages(messages, "warning");
      }
    },
    [services],
  );

  const changeDispatch = useCallback(
    (dispatch: string) => {
      const details = prices[dispatch];
      const quantityRequested = getQuantityRequested(details.price, details.cartItem);
      const quantityWithCorrections = getQuantityCorrected({
        requestedQuantity: quantityRequested,
        factorOrderQuantity: details.factorOrderQuantity,
        maxOrderQuantity: details.maxOrderQuantity,
        minOrderQuantity: details.minOrderQuantity,
        check: {
          factorOrder: true,
          maxOrder: true,
          minOrder: true,
        },
      });
      const quantity = quantityWithCorrections.quantity;

      setState((prev) => ({
        ...prev,
        dispatch,
        quantity,
      }));

      showMesssages({
        factorOrderQuantity: details.factorOrderQuantity,
        corrections: quantityWithCorrections.corrections,
      });
    },
    [setState],
  );

  const renderSituable = (type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM) => {
    return <SiutableFor type={type} data={situableFor} />;
  };

  const renderStatus = (type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM) => {
    return <ProductStatus status={stockStatus} type={type} />;
  };

  const renderPrice = (divideCurrencySymbol = false, className: string) => {
    return (
      <PriceValue
        className={className}
        amount={price?.price.amount}
        isVatApplicable={true}
        isDisable={!availableForOrder || hasProperty("outOfStock") || hasProperty("onRequest")}
        divideCurrencySymbol={divideCurrencySymbol}
      />
    );
  };

  const renderShipping = () => {
    return <ShippingNotify />;
  };

  const renderShippingRates = (type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM) => {
    return <ShippingRates type={type} productId={product.id} chainHash={price?.id} quantity={state.current?.quantity ?? product.minimalOrderQuantity ?? 1} />;
  };

  const renderVat = () => {
    return <VatNotify />;
  };

  const renderAdditionalBlock = () => {
    if (isOverMaxQuantity && product?.exchangesFinal && product?.exchangesFinal.length > 1) {
      return <OnRequest dispatchSelectedQuantity={selections.quantity} />;
    }

    if (isOverMaxQuantity && product?.exchangesFinal && product?.exchangesFinal.length === 1) {
      return <ExchangesAddToCart product={product} dispatchSelectedQuantity={selections.quantity} />;
    }

    return <></>;
  };

  const getConditionLeadForm = () => {
    if (hasProperty("onRequest")) {
      return LeadFormCondition.OnRequest;
    }

    if (hasProperty("noPrice")) {
      return LeadFormCondition.WithoutPrice;
    }

    if (hasProperty("outOfStock")) {
      return LeadFormCondition.OutOfStock;
    }
  };

  const renderTitle = () => {
    if (hasProperty("onRequest")) {
      return t("v8ae7i4wfguq8w9f.contact-us", "Contact us");
    }

    return "";
  };

  const getDataLeadForm = () => {
    if (getConditionLeadForm() === LeadFormCondition.OnRequest) {
      return {
        title: t("gq784fiqbw4f9iqw.get-in-touch", "Get in touch"),
        subTitle: t("fn8a94iufb9apwif.leave-your-contact", "Leave your contact details and we’ll get back to you"),
        buttonText: t("r89eiarugh89a4aw.submit", "Submit"),
        formName: "on_request_product_form",
        buttonIcon: null,
      };
    }

    if (getConditionLeadForm() === LeadFormCondition.WithoutPrice) {
      return {
        title: t("msufigvh8349r8q3.price-request", "Price request"),
        subTitle: t("ba7e8fi37wuagfbp.leave-your-contact-2", "Leave your contact details and we’ll get back to you with the latest price and delivery options"),
        buttonText: t("v78oiaw4fbqqowie.get-a-quote", "Get a quote"),
        formName: "without_price_product_form",
        buttonIcon: null,
      };
    }

    if (getConditionLeadForm() === LeadFormCondition.OutOfStock) {
      return {
        title: t("aaawbf7083oagw43.notify-me", "Notify me when back in stock"),
        subTitle: t(
          "aw48fgha9rguh9pa.leave-your-contact-3",
          "Be the first to know when this item is back - leave your contact details and we’ll send you update",
        ),
        buttonText: t("b78r0eig7fb7i4gf.get-availability-alert", "Get availability alert"),
        formName: "out_of_stock_product_form",
        buttonIcon: alertIcon,
      };
    }

    return false;
  };

  const changeQuantity = useCallback(
    (value: number) => {
      const dispatch = state.current.dispatch;
      const price = product.prices.find((price) => price.id === dispatch)!;
      const details = getPriceDetails(price);
      const quantityWithCorrections = getQuantityCorrected({
        requestedQuantity: value,
        factorOrderQuantity: details.factorOrderQuantity,
        maxOrderQuantity: details.maxOrderQuantity,
        minOrderQuantity: details.minOrderQuantity,
        check: {
          maxOrder: true,
        },
      });

      setState((prev) => ({
        ...prev,
        quantity: quantityWithCorrections.quantity,
      }));

      if (!details.cartItem.added) {
        addToCart();
      }

      showMesssages({
        factorOrderQuantity: details.factorOrderQuantity,
        corrections: quantityWithCorrections.corrections,
      });
    },
    [setState],
  );

  const addToCart = useCallback(async () => {
    const inCartState = services.cart.getItem(product.id, state.current.dispatch);
    const details = prices[state.current.dispatch];
    const price = details.price;
    const addedQuanity = inCartState.added ? inCartState.quantity : 0;
    const selectedQuantity = state.current.quantity;

    let quantity = 0;

    if (price && price.availableStockCount && price.availableStockCount < selectedQuantity) {
      quantity = price.availableStockCount + addedQuanity;
    } else {
      quantity = selectedQuantity + addedQuanity;
    }

    Gtag.pushEventEcommerce("add_to_cart", {
      items: [
        {
          item_name: getItemName(product),
          item_id: product.id,
          price: parseFloat(price.price.amount),
          item_brand: product.brand.name,
          index: 0,
          quantity: quantity,
          ...getCategoriesEcommerceItems(product.categories, services),
        },
      ],
    });

    setState((prev) => ({
      ...prev,
      quantity,
    }));

    await services.cart.updateItem({
      cartProductId: details.cartItem.cartProductId,
      productId: product!.id,
      priceId: price.id,
      quantity: quantity,
    });
  }, [setState]);

  type PriceData = {
    price: B2cProductPrice | null;
    minOrderQuantity: number;
    shipping: boolean;
    isLocalStockSupplier: boolean;
    priceStockType: string | null;
  };

  const getPriceData = (): PriceData => {
    if (!state?.current?.dispatch) {
      return {
        price: null,
        minOrderQuantity: 0,
        shipping: false,
        isLocalStockSupplier: false,
        priceStockType: null,
      };
    }

    const price = prices[state.current.dispatch];

    return {
      price: price.price,
      minOrderQuantity: price.minOrderQuantity,
      shipping: Boolean(state.current.dispatch),
      isLocalStockSupplier: price.price.isLocalStockSupplier,
      priceStockType: price.price.stockType,
    };
  };

  const { price, minOrderQuantity, shipping, priceStockType } = getPriceData();

  const cartItem = services.cart.getItem(product.id, price?.id ?? null);

  const mpn = useMemo(() => getMpn(product), [product]);
  const title = useMemo(() => getTitle(product), [product]);
  const filteredCrosses = useMemo(() => getFilteredCrosses(product), [product]);
  const specifications = useMemo(() => getSpecifications(product, t), [product, t]);
  const situableFor = useMemo(() => getSituableFor(product), [product]);
  const images = useMemo(() => getImages(product), [product]);
  const availableForOrder = useMemo(() => getAvailableForOrder(product), [product]);
  const stockStatus = useMemo(
    () => getStockStatus(
      availableForOrder,
      priceStockType,
      getInitialPrice(),
      services.customerChannel.deliveryTermsMode,
    ),
    [product, availableForOrder, priceStockType, services.customerChannel.deliveryTermsMode],
  );
  const redirectMpn = useMemo(() => getRedirectedMpn(window.location.search, store.state.originalMpn), [window.location.search, store.state.originalMpn]);
  const showFits = false;
  const showRating = false;
  const isAddedToCart = cartItem.added;

  const goToCart = () => {
    Gtag.pushEvent("go_to_cart", "Product Page");
    location.route(Url.to("cart-page"));
  };

  const addToCartClick = () => {
    if (isAddedToCart) {
      goToCart();
    } else {
      addToCart();
    }
  };

  const hasProperty = (
    type: "isHidePriceIfProductIsOutOfStock" | "deliveryStandardTimesIsNotDefined" | "onRequest" | "noPrice" | "outOfStock" | "hasPrice",
  ): boolean => {
    const price = getInitialPrice();
    const deliveryTerm = price ? getStandardDeliveryTermWindow(price, services.customerChannel.deliveryTermsMode) : null;
    const deliveryStandardTimesIsNotDefined = deliveryTerm
      ? deliveryTerm.from == null && deliveryTerm.to == null
      : false;
    const isHidePriceIfProductIsOutOfStock = price && price.isHidePriceIfProductIsOutOfStock;
    const stockTypeProperty = price && price.stockType;

    switch (type) {
      case "isHidePriceIfProductIsOutOfStock":
        return isHidePriceIfProductIsOutOfStock;
      case "deliveryStandardTimesIsNotDefined":
        return deliveryStandardTimesIsNotDefined;
      case "onRequest":
        return product.state === ProductStateType.OnRequest;
      case "noPrice":
        return !price;
      case "outOfStock":
        return defineOutOfStock({
          hasPrice: !!price,
          isHidePriceIfProductIsOutOfStock: isHidePriceIfProductIsOutOfStock,
          deliveryStandardTimesIsNotDefined,
          stockType: stockTypeProperty,
          productState: product.state,
        });
      case "hasPrice":
        return !!price;
      default:
        return false;
    }
  };

  const googleEventData = useMemo(() => {
    return [
      {
        item_name: getItemName(product),
        item_id: product.id,
        price: !(hasProperty("outOfStock") || hasProperty("noPrice") || hasProperty("onRequest")) ? parseFloat(price?.price.amount ?? 0) : 0,
        item_brand: product.brand.name,
        index: 0,
        quantity: "1",
        ...getCategoriesEcommerceItems(product.categories, services),
      },
    ];
  }, []);

  const googleEventDataCrosses = useMemo(() => {
    return filteredCrosses.map((product, i) => {
      return {
        item_name: getItemName(product),
        item_id: product.id,
        price: product.prices[0].price.amount,
        item_brand: product.brand.name,
        index: i,
        quantity: "1",
        item_list_name: "Analogues",
        ...getCategoriesEcommerceItems(product.categories, services),
      };
    });
  }, []);

  useEffect(() => {
    Gtag.pushEventEcommerce("view_item", {
      items: googleEventData,
    });

    if (googleEventDataCrosses.length > 0) {
      Gtag.pushEventEcommerce("view_item_list", {
        items: googleEventDataCrosses,
      });
    }
  }, []);

  const hasBlock = (
    type:
      | "can-be"
      | "situable-for"
      | "shipping-notify"
      | "product-dispatch"
      | "carousel"
      | "specifications"
      | "lg.presentation"
      | "quantity-tip"
      | "quantity-input"
      | "discount"
      | "stock"
      | "about",
  ): boolean => {
    switch (type) {
      case "can-be":
        return filteredCrosses.length > 0;
      case "situable-for":
        return situableFor.length > 0 && false;
      case "shipping-notify":
        return Boolean(shipping);
      case "product-dispatch":
        return Boolean(product.prices.length);
      case "carousel":
        return images.length > 0;
      case "specifications":
        return specifications.length > 0;
      case "lg.presentation":
        return hasBlock("carousel") || hasBlock("specifications");
      case "quantity-tip":
        return minOrderQuantity > 1 && availableForOrder && !hasProperty("outOfStock") && !hasProperty("onRequest");
      case "quantity-input":
        return isAddedToCart;
      case "discount":
        return true;
      case "stock":
        return true;
      case "about":
        return !!(product.productInfo.description?.length || product.productInfoAdditional.description?.length);
      default:
        return false;
    }
  };

  const metaDataset = useMemo(() => {
    const productName = product!.productInfo.name;
    const brandName = product!.brand.name;
    const productDesctiption = product?.productInfo.description;

    const title = services.pagemeta.createProductTitle({
      mpn,
      productName,
      brandName,
    });
    const description = services.pagemeta.createProductDescription({
      productName,
      brandName,
      productDesctiption,
    });

    return { title, description };
  }, [product]);

  const jsonLdDataset: JsonLdDataset = useMemo(() => {
    // don't create rich results if product has no prices
    if (!product) {
      return {};
    }

    const id = product.id;
    const name = title;
    const description = getDescriptionText();
    const brand = product.brand.name;
    const image = images[0]?.url ?? noImageProductUrl;
    const url = window.location.href;
    // same order as in the product page
    const prices = dispatchItems()?.map((item) => {
      const price = product.prices.find((price) => price.id === item.id)!;
      return {
        money: price.price,
        isLocalStockSupplier: price.isLocalStockSupplier,
      };
    });

    return {
      product: {
        id: id,
        mpn: mpn,
        url: url,
        name: name,
        description: description,
        image: image,
        brand: brand,
        manufacturer: brand,
        prices: prices,
        state: product.state,
        exchanges: product.exchangesFinal,
      },
      page: {
        title: metaDataset.title,
      },
    };
  }, [product, metaDataset, price, images]);

  return {
    product,
    stockStatus,
    specifications,
    redirectMpn,
    title,
    mpn,
    showRating,
    showFits,
    jsonLdDataset,
    metaDataset,
    seoMarkupDataset,
    hasBlock,
    hasProperty,
    addToCart,
    changeDispatch,
    changeQuantity,
    noImageProductUrl,
    renderDispatch,
    renderAdd,
    renderQuantity,
    getHasAvailableInStock,
    renderProductDiscontinuedSegment,
    renderCanBe,
    renderDiscount,
    renderBadge,
    renderStatus,
    renderPrice,
    renderTip,
    renderVat,
    renderShipping,
    renderShippingRates,
    renderAdditionalBlock,
    renderAbout,
    renderSituable,
    renderCarousel,
    configureSelections,
    renderTitle,
    getDataLeadForm,
  };
}

const PRODUCT_STATE_ACTIVE = 1;
const PRODUCT_STATE_ON_REQUEST = 2;
